import { D2CFunnelStep, D2CSourceApp } from '@orcar/common';
import { createFunnelTracker, type FunnelTracker } from '@orcar/common-d2c';
import mixpanel from 'mixpanel-browser';

// 이벤트 이름 리터럴 타입
export type AnalyticsEventName =
  | 'page_view'
  | 'homepage_viewed'
  | 'search_started'
  | 'car_detailed_viewed'
  | 'booking_started'
  | 'reservation_created'
  | 'payment_successful'
  | 'payment_failed'
  | 'checkout_started'
  | 'guest_checkout_clicked'
  | 'signup_discount_clicked'
  | 'signup_started'
  | 'signup'
  | 'sms_clicked'
  | 'banner_clicked'
  | 'button_clicked';

// 공통 속성 타입 정의
export type Platform = 'web';
export type DeviceType = 'mobile' | 'desktop';
export type UserType = 'guest' | 'member';
export type Channel =
  | 'direct'
  | 'organic'
  | 'social'
  | 'referral'
  | 'facebook_ads'
  | 'google_ads'
  | 'kakao'
  | 'sms';

// 공통 이벤트 속성 인터페이스
export interface CommonEventProperties {
  app_name: string;
  platform: Platform;
  device: DeviceType;
  user_type: UserType;
  channel: Channel;
  environment: 'prod' | 'dev';
}

// 기본 이벤트 속성 인터페이스
export interface BaseEventProperties {
  timestamp?: number;
  [key: string]: unknown;
}

// 페이지 뷰 이벤트 속성
export interface PageViewEventProperties extends BaseEventProperties {
  page_name: string;
  utm_source?: string;
  utm_campaign?: string;
  utm_medium?: string;
  campaign_channel?: string;
}

// 검색 이벤트 속성
export interface SearchEventProperties extends BaseEventProperties {
  pickUpDate?: string;
  dropOffDate?: string;
  location?: string;
}

// 차량 상세 이벤트 속성
export interface CarDetailEventProperties extends BaseEventProperties {
  car_id: string;
  car_model: string;
  car_type: string;
}

// 버튼 클릭 이벤트 속성
export interface ButtonClickEventProperties extends BaseEventProperties {
  button_name: string;
  page: string;
}

// 예약 시작 이벤트 속성
export interface BookingStartedEventProperties extends BaseEventProperties {
  car_id: string;
  car_type: string;
  dates: string;
}

// 예약 생성 이벤트 속성
export interface ReservationEventProperties extends BaseEventProperties {
  reservation_id: string;
  car_id: string;
  car_type: string;
  total_amount: number;
}

// 홈페이지 방문 이벤트 속성
export interface HomepageViewedEventProperties extends BaseEventProperties {
  source?: string;
  referrer?: string;
}

// 체크아웃 시작 이벤트 속성
export interface CheckoutStartedEventProperties extends BaseEventProperties {
  guest_checkout: boolean;
  reservation_id?: string;
}

// 회원가입 이벤트 속성
export interface SignupEventProperties extends BaseEventProperties {
  from_discount_cta: boolean;
  user_id?: string;
}

// 모든 이벤트 속성 타입의 유니온
export type EventProperties =
  | PageViewEventProperties
  | SearchEventProperties
  | CarDetailEventProperties
  | ButtonClickEventProperties
  | ReservationEventProperties
  | HomepageViewedEventProperties
  | BookingStartedEventProperties
  | CheckoutStartedEventProperties
  | SignupEventProperties;

// Mixpanel 초기화 상태
let isInitialized = false;

// 디바이스 타입 감지
function detectDevice(): DeviceType {
  if (typeof window === 'undefined') return 'desktop';

  const userAgent = window.navigator.userAgent.toLowerCase();
  const mobileKeywords = ['mobile', 'android', 'iphone'];

  return mobileKeywords.some((keyword) => userAgent.includes(keyword))
    ? 'mobile'
    : 'desktop';
}

// 사용자 타입 감지
function detectUserType(): UserType {
  if (typeof window === 'undefined') return 'guest';

  // localStorage에서 인증 상태 확인
  const authStorage = localStorage.getItem('auth-storage');
  if (!authStorage) return 'guest';

  try {
    const authData = JSON.parse(authStorage);
    return authData?.state?.isLoggedIn ? 'member' : 'guest';
  } catch {
    return 'guest';
  }
}

// 채널 감지 (UTM 기반)
function detectChannel(): Channel {
  if (typeof window === 'undefined') return 'direct';

  // localStorage에서 UTM 정보 확인
  const utmSource = localStorage.getItem('utm_source');
  const utmMedium = localStorage.getItem('utm_medium');

  if (!utmSource) return 'direct';

  // UTM 기반 채널 매핑
  if (utmSource.includes('facebook')) return 'facebook_ads';
  if (utmSource.includes('google')) return 'google_ads';
  if (utmSource.includes('kakao')) return 'kakao';
  if (utmSource.includes('sms')) return 'sms';

  // Medium 기반 분류
  if (utmMedium && utmMedium === 'social') return 'social';
  if (utmMedium && utmMedium === 'organic') return 'organic';
  if (utmMedium && utmMedium === 'referral') return 'referral';

  return 'direct';
}

// 공통 이벤트 속성 생성
function getCommonProperties(): CommonEventProperties {
  const productionDomains = {
    rainbow: ['rainbowrent.co.kr', 'www.rainbowrent.co.kr'],
  };

  const appName = import.meta.env.VITE_PACKAGE_NAME || 'rainbow';
  const currentDomains =
    productionDomains[appName as keyof typeof productionDomains] || [];
  const isProduction = currentDomains.includes(window.location.hostname);

  return {
    app_name: appName,
    platform: 'web' as Platform,
    device: detectDevice(),
    user_type: detectUserType(),
    channel: detectChannel(),
    environment: isProduction ? 'prod' : 'dev',
  };
}

// 안전하게 Mixpanel 호출하는 유틸리티
const safeMixpanelCall = (callback: () => void): void => {
  try {
    callback();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Analytics error:', error);
  }
};

// UTM 파라미터 처리 함수
export function captureUtmParameters(): void {
  if (typeof window === 'undefined') return;

  const urlParams = new URLSearchParams(window.location.search);
  const utmParams = {
    utm_source: urlParams.get('utm_source'),
    utm_medium: urlParams.get('utm_medium'),
    utm_campaign: urlParams.get('utm_campaign'),
    campaign_channel: urlParams.get('campaign_channel'),
  };

  // UTM 파라미터가 있는 경우에만 localStorage에 저장
  Object.entries(utmParams).forEach(([key, value]) => {
    if (value) {
      localStorage.setItem(key, value);
    }
  });

  // Mixpanel에도 UTM 정보 등록 (슈퍼 프로퍼티로)
  if (isInitialized) {
    const validUtmParams = Object.fromEntries(
      Object.entries(utmParams).filter(([_, value]) => value !== null),
    );

    if (Object.keys(validUtmParams).length > 0) {
      registerProperties(validUtmParams);
    }
  }
}

// 초기화 함수
export function initAnalytics(): void {
  // 실제 도메인 기반으로 환경 판단
  const productionDomains = ['rainbowrent.co.kr', 'www.rainbowrent.co.kr'];
  const isProduction = productionDomains.includes(window.location.hostname);

  // 통합된 Mixpanel 토큰 사용
  const token = import.meta.env.VITE_MIXPANEL_TOKEN;

  if (!token) {
    return;
  }

  safeMixpanelCall(() => {
    // Mixpanel 초기화
    mixpanel.init(token, {
      debug: import.meta.env.MODE === 'development',
      track_pageview: false,
      record_sessions_percent: 0, // 세션 리플레이는 Clarity로 본다
      autocapture: {
        pageview: 'full-url', // 전체 URL로 페이지뷰 캡처
        click: true, // 클릭 추적 활성화
        input: true, // 입력 필드 추적 활성화
        scroll: true, // 스크롤 추적 활성화
        submit: true, // 폼 제출 추적 활성화
        capture_text_content: false, // 텍스트 내용 캡처 비활성화 (개인정보 보호)
      },
    });

    mixpanel.register({
      environment: isProduction ? 'prod' : 'dev',
    });

    isInitialized = true;
  });
}

/** 예약 확정 요청(`/pre-reservations/:id/confirm`)과 퍼널 이벤트에 실을 유입 정보. */
export function getReservationAttribution(): {
  sourceApp: D2CSourceApp;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
} {
  const read = (key: string) =>
    (typeof window === 'undefined' ? null : localStorage.getItem(key)) ||
    undefined;

  return {
    sourceApp: D2CSourceApp.RAINBOW,
    utmSource: read('utm_source'),
    utmMedium: read('utm_medium'),
    utmCampaign: read('utm_campaign'),
  };
}

/**
 * Mixpanel 이벤트를 자사 예약 퍼널 단계로 매핑한다(제주로·갤럭시와 같은 단계).
 * 여기 있는 이벤트만 `d2c_funnel_event`로도 전송된다.
 */
const EVENT_TO_FUNNEL_STEP: Partial<Record<AnalyticsEventName, D2CFunnelStep>> =
  {
    page_view: D2CFunnelStep.SESSION_ENTRY,
    homepage_viewed: D2CFunnelStep.SESSION_ENTRY,
    search_started: D2CFunnelStep.SEARCH_STARTED,
    car_detailed_viewed: D2CFunnelStep.VEHICLE_VIEWED,
    booking_started: D2CFunnelStep.BOOKING_STARTED,
    reservation_created: D2CFunnelStep.RESERVATION_CREATED,
  };

let funnelTracker: FunnelTracker | null = null;
function sendFunnelStep(step: D2CFunnelStep): void {
  funnelTracker ??= createFunnelTracker({
    endpoint: `${import.meta.env.VITE_SERVER_URL}/funnel-events`,
    companyId: Number(import.meta.env.VITE_COMPANY_ID),
    sourceApp: D2CSourceApp.RAINBOW,
    getAttribution: getReservationAttribution,
  });
  funnelTracker.trackStep(step);
}

function trackFunnelStep(eventName: AnalyticsEventName): void {
  const step = EVENT_TO_FUNNEL_STEP[eventName];
  if (step) sendFunnelStep(step);
}

/**
 * 결제 화면에서 손님이 고객 정보를 제출하는 결제(현장 결제는 예약) 버튼을 누른 순간을
 * 자사 퍼널에만 남긴다(Mixpanel 이벤트 없음). 확인 창·저장 요청보다 먼저 부른다.
 */
export function trackPaymentStarted(): void {
  sendFunnelStep(D2CFunnelStep.PAYMENT_STARTED);
}

// 이벤트 추적 (타입 안전) - 공통 속성 자동 추가
export function trackEvent(
  eventName: AnalyticsEventName,
  properties: Partial<EventProperties>,
): void {
  // 자사 퍼널 전송은 Mixpanel 초기화와 무관하게 항상 먼저 수행한다.
  trackFunnelStep(eventName);

  if (!isInitialized) {
    return;
  }

  const commonProps = getCommonProperties();

  safeMixpanelCall(() => {
    mixpanel.track(eventName, {
      ...commonProps,
      ...properties,
      timestamp: Date.now(),
    });
  });
}

// 슈퍼 프로퍼티 등록
export function registerProperties(properties: Record<string, unknown>): void {
  if (!isInitialized) return;

  safeMixpanelCall(() => {
    mixpanel.register(properties);
  });
}

// 세션 리셋
export function reset(): void {
  if (!isInitialized) return;

  safeMixpanelCall(() => {
    mixpanel.reset();
  });
}

// 사용자 식별 함수
export function identifyUser(
  userId: string,
  userProperties?: Record<string, unknown>,
): void {
  if (!isInitialized) {
    return;
  }

  safeMixpanelCall(() => {
    mixpanel.identify(userId);

    if (userProperties) {
      mixpanel.people.set(userProperties);
    }
  });
}

// 사용자 프로퍼티 설정
export function setUserProperties(properties: Record<string, unknown>): void {
  if (!isInitialized) {
    return;
  }

  safeMixpanelCall(() => {
    mixpanel.people.set(properties);
  });
}

// 요구사항에 따른 이벤트 추적 함수들
export function trackHomepageViewed(
  properties: Partial<HomepageViewedEventProperties> = {},
): void {
  trackEvent('homepage_viewed', properties);
}

export function trackSearchStarted(
  properties: Partial<SearchEventProperties> = {},
): void {
  trackEvent('search_started', properties);
}

export function trackCarDetailedViewed(
  properties: Partial<CarDetailEventProperties> = {},
): void {
  trackEvent('car_detailed_viewed', properties);
}

export function trackBookingStarted(
  properties: Partial<BookingStartedEventProperties> = {},
): void {
  trackEvent('booking_started', properties);
}

export function trackReservationCreated(
  properties: Partial<ReservationEventProperties> = {},
): void {
  trackEvent('reservation_created', properties);
}

export function trackPaymentSuccessful(
  properties: Partial<BaseEventProperties> = {},
): void {
  trackEvent('payment_successful', properties);
}

export function trackPaymentFailed(
  properties: Partial<BaseEventProperties> = {},
): void {
  trackEvent('payment_failed', properties);
}

export function trackCheckoutStarted(
  properties: Partial<CheckoutStartedEventProperties> = {},
): void {
  trackEvent('checkout_started', {
    guest_checkout: true,
    ...properties,
  });
}

export function trackGuestCheckoutClicked(
  properties: Partial<BaseEventProperties> = {},
): void {
  trackEvent('guest_checkout_clicked', properties);
}

export function trackSignupDiscountClicked(
  properties: Partial<BaseEventProperties> = {},
): void {
  trackEvent('signup_discount_clicked', properties);
}

export function trackSignupStarted(
  provider: 'naver' | 'kakao',
  properties: Partial<BaseEventProperties> = {},
): void {
  trackEvent('signup_started', {
    ...properties,
    provider,
  });
}

export function trackSignup(
  properties: Partial<SignupEventProperties> = {},
): void {
  trackEvent('signup', {
    from_discount_cta: true,
    ...properties,
  });
}

export function trackSmsClicked(
  properties: Partial<BaseEventProperties> = {},
): void {
  trackEvent('sms_clicked', properties);
}

export function trackBannerClicked(
  properties: Partial<BaseEventProperties> = {},
): void {
  trackEvent('banner_clicked', properties);
}

export function trackButtonClick(
  buttonName: string,
  properties: Partial<ButtonClickEventProperties> = {},
): void {
  trackEvent('button_clicked', {
    button_name: buttonName,
    ...properties,
  });
}

// 페이지 뷰 추적
export function trackPageView(
  pageName: string,
  properties: Partial<PageViewEventProperties> = {},
): void {
  trackEvent('page_view', {
    page_name: pageName,
    ...properties,
  });
}
